import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    let newMainImage;
    let oldMainImage;

    if (window.innerWidth >= 750) {
      newMainImage = newMediaGallery.querySelector('.media-gallery__grid li:first-of-type .product-media img');
    } else {
      newMainImage = newMediaGallery.querySelector('slideshow-component slideshow-slide:first-of-type .product-media img');
    }
    if (!newMainImage) return;
    let sharedParent = event.target.closest('.product-information');
    if (!sharedParent) {
      sharedParent = event.target.closest('.quick-add-modal__content');

      if (!sharedParent) return;
    }
    if (window.innerWidth >= 750) {
      oldMainImage = sharedParent.querySelector('.media-gallery__grid li:first-of-type .product-media img');
    } else {
      oldMainImage = sharedParent.querySelector('slideshow-component slideshow-slide:first-of-type .product-media img');
    }
    if (!oldMainImage) return;
    oldMainImage.style.transition = "opacity 0.4s ease-in";
    oldMainImage.style.opacity = "0.5";
    
    oldMainImage.addEventListener("transitionend", () => {
      newMainImage.style.opacity = "0.5";
      newMainImage.style.transition = "opacity 0.4s ease-out";
      this.replaceWith(newMediaGallery);
      requestAnimationFrame(() => {
        newMainImage.style.opacity = "1";
      });
    })
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
